import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Plus, Pencil, Trash2, Search, Clock, CheckCircle2, ChevronDown, ChevronRight, Archive } from "lucide-react";
import { toast } from "sonner";
import { Dialog, Input } from "./Suppliers";
import { useAuth } from "../context/AuthContext";
import { usePager } from "../components/Pagination";
import { fmtIST } from "../lib/time";
import useSubmitGuard from "../lib/useSubmitGuard";
import { fmtQty } from "../lib/format";

const MONTHS = ["", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

const TODAY = () => new Date().toISOString().slice(0, 10);
const EMPTY = () => ({
  supplier_id: "",
  // Feb-2026: supplier-wise multi-material. Items array holds one row per material.
  items: [{ material_id: "", expected_qty: 0 }],
  period: "monthly",
  month: new Date().getMonth() + 1, year: new Date().getFullYear(),
  start_date: "", end_date: "", period_label: "",
  edit_remark: "",
  // Legacy single-material edit support
  material_id: "", expected_qty: 0,
});

export default function Schedules() {
  const { busy: submitBusy, guard: submitGuard, reset: resetGuard } = useSubmitGuard();
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY());
  const [q, setQ] = useState("");
  const [reviewOf, setReviewOf] = useState(null);
  const [viewOf, setViewOf] = useState(null);  // Feb-2026: click number to view full schedule details
  const [includeArchived, setIncludeArchived] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());  // group keys
  const { user } = useAuth();
  const isSA = !!user?.role?.is_super_admin;
  const isPHorSA = isSA || user?.role?.name === "Plant Head/Admin";

  const load = async () => {
    const [sc, s, m] = await Promise.all([
      api.get(`/schedules${includeArchived ? "?include_archived=true" : ""}`),
      api.get("/suppliers"), api.get("/materials"),
    ]);
    setRows(sc.data); setSuppliers(s.data); setMaterials(m.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeArchived]);

  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "—";
  const matName = (id) => materials.find((m) => m.id === id)?.name || "—";
  const matCode = (id) => materials.find((m) => m.id === id)?.code || "";
  const filteredMaterials = form.supplier_id ? materials.filter((m) => (m.supplier_ids || []).includes(form.supplier_id)) : materials;

  const filtered = useMemo(() => {
    const k = q.toLowerCase();
    if (!k) return rows;
    return rows.filter((r) =>
      r.number?.toLowerCase().includes(k) ||
      supName(r.supplier_id).toLowerCase().includes(k) ||
      matName(r.material_id).toLowerCase().includes(k) ||
      matCode(r.material_id).toLowerCase().includes(k) ||
      (r.period_label || "").toLowerCase().includes(k)
    );
  }, [rows, q, suppliers, materials]);

  // Wave-J: group by Month + Year. Weekly schedules nest under the month
  // of their start_date. Keys are sorted DESC so the current month is first.
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      let y, m;
      if (r.period === "monthly") { y = r.year; m = r.month; }
      else if (r.start_date) { const d = new Date(r.start_date); y = d.getFullYear(); m = d.getMonth() + 1; }
      else { y = new Date().getFullYear(); m = new Date().getMonth() + 1; }
      const key = `${y}-${String(m).padStart(2, "0")}`;
      const label = `${MONTHS[m]} ${y}`;
      if (!map.has(key)) map.set(key, { key, label, rows: [], year: y, month: m });
      map.get(key).rows.push(r);
    }
    return [...map.values()].sort((a, b) => b.key.localeCompare(a.key));
  }, [filtered]);

  // Auto-expand the latest two groups on first render so users see the active stuff
  useEffect(() => {
    if (groups.length && expanded.size === 0) {
      setExpanded(new Set(groups.slice(0, 2).map((g) => g.key)));
    }
    // eslint-disable-next-line
  }, [groups.length]);

  const toggleGroup = (k) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  const activateDraft = async (r) => {
    try {
      await api.post(`/schedules/${r.id}/activate`);
      toast.success(`Activated · ${r.number}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Activate failed"); }
  };

  const runRolloverNow = async () => {
    try {
      const r = await api.post("/schedules/run-rollover-now");
      toast.success(`Rollover done · ${r.data.created} new draft(s), ${r.data.archived} archived`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Rollover failed"); }
  };

  const save = async () => {
    if (!form.supplier_id) { toast.error("Supplier required"); return; }
    if (form.period === "monthly" && (!form.month || !form.year)) { toast.error("Month and year required"); return; }
    if (form.period === "weekly" && (!form.start_date || !form.end_date)) { toast.error("Start and end dates required"); return; }
    if (form.period === "weekly" && form.start_date > form.end_date) { toast.error("End date must be after start date"); return; }
    if (editing && !(form.edit_remark || "").trim()) { toast.error("Edit remark is required"); return; }
    try {
      const basePayload = {
        supplier_id: form.supplier_id,
        period: form.period,
        period_label: form.period_label || "",
        month: form.period === "monthly" ? +form.month : null,
        year: form.period === "monthly" ? +form.year : null,
        start_date: form.period === "weekly" ? form.start_date : null,
        end_date: form.period === "weekly" ? form.end_date : null,
        edit_remark: form.edit_remark || undefined,
      };
      await submitGuard(async (idemKey) => {
        if (editing) {
          if (!form.material_id) throw new Error("Material required");
          await api.put(`/schedules/${editing}`, {
            ...basePayload, material_id: form.material_id, expected_qty: +form.expected_qty,
          }, { headers: { "Idempotency-Key": idemKey } });
        } else {
          const items = (form.items || []).filter((i) => i.material_id && +i.expected_qty >= 0);
          if (items.length === 0) throw new Error("Add at least one material with expected qty");
          await api.post("/schedules", {
            ...basePayload,
            items: items.map((i) => ({ material_id: i.material_id, expected_qty: +i.expected_qty })),
          }, { headers: { "Idempotency-Key": idemKey } });
        }
      });
      setOpen(false);
      toast.success(editing ? (isSA ? "Schedule updated" : "Edit submitted for SA approval") : "Schedule(s) created");
      resetGuard();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const addLineItem = () => setForm((f) => ({ ...f, items: [...(f.items || []), { material_id: "", expected_qty: 0 }] }));
  const removeLineItem = (idx) => setForm((f) => ({ ...f, items: (f.items || []).filter((_, i) => i !== idx) }));
  const updateLineItem = (idx, patch) => setForm((f) => ({
    ...f, items: (f.items || []).map((it, i) => i === idx ? { ...it, ...patch } : it),
  }));

  const approveEdit = async (r) => {
    try { await api.post(`/schedules/${r.id}/approve-edit`); toast.success(`${r.number} edit applied`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const rejectEdit = async () => {
    if (!(reviewOf?.remark || "").trim()) { toast.error("Reject reason required"); return; }
    try { await api.post(`/schedules/${reviewOf.id}/reject-edit`, { remark: reviewOf.remark }); toast.success("Edit rejected"); setReviewOf(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete schedule ${r.number}?`)) return;
    try { await api.delete(`/schedules/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const periodDisplay = (r) => {
    if (r.period_label) return r.period_label;
    if (r.period === "monthly" && r.month && r.year) return `${MONTHS[r.month]} ${r.year}`;
    if (r.period === "weekly" && r.start_date && r.end_date) return `${r.start_date} → ${r.end_date}`;
    return "—";
  };

  const yrs = [];
  const cy = new Date().getFullYear();
  for (let y = cy - 1; y <= cy + 2; y++) yrs.push(y);

  return (
    <div data-testid="schedules-page">
      <PageHeader title="Schedules" subtitle="Weekly / monthly supplier material plans · grouped by month"
        helpText="Schedules drive variance flagging at gate entry and the Abnormal Report. Monthly: pick month + year. Weekly: pick start + end date and (optionally) a custom label like 'Week 1' or 'Week A'. Schedules older than 6 months auto-archive."
        right={
          <div className="flex items-center gap-2">
            {isSA && <button onClick={runRolloverNow} className="text-[10px] tracking-widest uppercase font-bold border border-[#2E7D32]/30 text-[#2E7D32] px-3 py-2 rounded-sm hover:bg-[#2E7D32]/5" data-testid="run-rollover-btn" title="Materialise this month's drafts from last month + archive >6mo">Run Rollover</button>}
            <button onClick={() => { setForm(EMPTY()); setEditing(null); setOpen(true); }} className="bg-[#1565C0] hover:bg-[#0D2B5E] text-white text-sm px-4 py-2 rounded-sm flex items-center gap-2 uppercase tracking-wider font-semibold" data-testid="new-schedule-btn"><Plus size={14}/> New Schedule</button>
          </div>
        }
      />
      <div className="p-8">
        <div className="mb-3 flex gap-3 items-center">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D2B5E]/40"/>
            <input data-testid="schedule-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by material, supplier, period, number…" className="w-full pl-9 pr-3 py-2 border border-[#EEEEEE] rounded-sm text-sm bg-white"/>
          </div>
          <label className="flex items-center gap-2 text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/70 cursor-pointer" data-testid="toggle-archived">
            <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)}/>
            <Archive size={12}/> Include archived (&gt;6 months)
          </label>
        </div>
        <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-hidden">
          {groups.length === 0 && <div className="p-10 text-center text-[#0D2B5E]/50">No schedules.</div>}
          {groups.map((g) => (
            <SchGroupBlock key={g.key} g={g} expanded={expanded.has(g.key)} onToggle={() => toggleGroup(g.key)}
              supName={supName} matCode={matCode} matName={matName} periodDisplay={periodDisplay}
              isSA={isSA} isPHorSA={isPHorSA} onActivate={activateDraft}
              onView={(r) => setViewOf(r)}
              onEdit={(r) => { setForm({ ...EMPTY(), ...r, month: r.month || new Date().getMonth() + 1, year: r.year || new Date().getFullYear(), edit_remark: "" }); setEditing(r.id); setOpen(true); }}
              onDelete={del} onApprove={approveEdit} onReject={(r) => setReviewOf({ id: r.id, number: r.number, pending: r.pending_edit, remark: "" })}/>
          ))}
        </div>
      </div>

      {open && (
        <Dialog title={editing ? "Edit Schedule" : "New Supplier Schedule"} onClose={() => setOpen(false)} wide>
          <div className="grid grid-cols-2 gap-4">
            <div className={editing ? "" : "col-span-2"}>
              <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Supplier *</label>
              <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value, items: [{ material_id: "", expected_qty: 0 }] })} data-testid="sch-supplier" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                <option value="">Select…</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            {editing && (
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Material *</label>
                <select value={form.material_id} onChange={(e) => setForm({ ...form, material_id: e.target.value })} disabled={!form.supplier_id} data-testid="sch-material" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm disabled:bg-[#F5F5F5]">
                  <option value="">Select…</option>
                  {filteredMaterials.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
                </select>
              </div>
            )}
            <div className="col-span-2">
              <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Period Type *</label>
              <div className="grid grid-cols-2 gap-2">
                {[["monthly", "Monthly (month + year)"], ["weekly", "Weekly (custom date range)"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setForm({ ...form, period: v })} data-testid={`sch-period-${v}`}
                    className={`px-3 py-2 text-xs rounded-sm uppercase tracking-widest font-bold border ${form.period === v ? "bg-[#1565C0] text-white border-[#1565C0]" : "bg-white text-[#0D2B5E] border-[#EEEEEE]"}`}>{l}</button>
                ))}
              </div>
            </div>
            {form.period === "monthly" && (
              <>
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Month *</label>
                  <select value={form.month} onChange={(e) => setForm({ ...form, month: +e.target.value })} data-testid="sch-month" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                    {MONTHS.slice(1).map((nm, i) => <option key={i + 1} value={i + 1}>{nm}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Year *</label>
                  <select value={form.year} onChange={(e) => setForm({ ...form, year: +e.target.value })} data-testid="sch-year" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                    {yrs.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </>
            )}
            {form.period === "weekly" && (
              <>
                <Input label="Start Date *" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testId="sch-start"/>
                <Input label="End Date *" type="date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testId="sch-end"/>
                <div className="col-span-2">
                  <Input label="Custom Label (optional, e.g. Week 1, Week A)" value={form.period_label} onChange={(v) => setForm({ ...form, period_label: v })} testId="sch-label"/>
                </div>
              </>
            )}
            {editing && (
              <Input label="Expected Qty *" type="number" value={form.expected_qty} onChange={(v) => setForm({ ...form, expected_qty: v })} testId="sch-qty"/>
            )}
          </div>

          {!editing && (
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Materials &amp; Expected Quantities *</label>
                <button type="button" onClick={addLineItem} disabled={!form.supplier_id} data-testid="sch-add-material"
                  className="text-[#1565C0] text-[10px] uppercase tracking-widest font-bold disabled:opacity-30">+ Add Material</button>
              </div>
              <table className="w-full text-sm border border-[#EEEEEE]">
                <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                  <tr>
                    <th className="text-left px-3 py-1.5">Material</th>
                    <th className="text-right px-3 py-1.5 w-32">Expected Qty</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {(form.items || []).map((it, i) => (
                    <tr key={i} className="border-t border-[#EEEEEE]">
                      <td className="p-1">
                        <select value={it.material_id} onChange={(e) => updateLineItem(i, { material_id: e.target.value })} disabled={!form.supplier_id}
                          data-testid={`sch-item-mat-${i}`} className="w-full border border-[#EEEEEE] px-2 py-1.5 text-xs rounded-sm disabled:bg-[#F5F5F5]">
                          <option value="">Select…</option>
                          {filteredMaterials.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
                        </select>
                      </td>
                      <td className="p-1">
                        <input type="number" min="0" value={it.expected_qty} onChange={(e) => updateLineItem(i, { expected_qty: e.target.value })}
                          data-testid={`sch-item-qty-${i}`} className="w-full border border-[#EEEEEE] px-2 py-1.5 text-xs rounded-sm font-mono text-right"/>
                      </td>
                      <td className="text-center">
                        {(form.items || []).length > 1 && <button onClick={() => removeLineItem(i)} className="text-[#C62828]">×</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-1 text-[10px] text-[#0D2B5E]/50">One schedule entry will be created per material — they share the supplier and period above.</div>
            </div>
          )}

          {editing && (
            <div className="mt-4">
              <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Edit Remark * {isSA ? "(applied immediately)" : "(submitted to SA for approval)"}</label>
              <textarea value={form.edit_remark || ""} onChange={(e) => setForm({ ...form, edit_remark: e.target.value })} rows={2} placeholder="Why is this schedule being changed?" data-testid="sch-edit-remark"
                className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm"/>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={save} disabled={submitBusy} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed" data-testid="save-schedule-btn">{submitBusy ? "Saving…" : "Save"}</button>
          </div>
        </Dialog>
      )}

      {reviewOf && (
        <Dialog title={`Reject edit · ${reviewOf.number}`} onClose={() => setReviewOf(null)}>
          <div className="text-xs text-[#0D2B5E]/70 bg-[#F5F5F5] p-3 rounded-sm mb-3 space-y-1">
            <div><strong>Submitted by:</strong> {reviewOf.pending?.submitted_by}</div>
            <div><strong>PH remark:</strong> {reviewOf.pending?.remark || "—"}</div>
            <div><strong>Diff:</strong> <span className="font-mono">{Object.keys(reviewOf.pending?.diff || {}).join(", ") || "—"}</span></div>
          </div>
          <textarea placeholder="Reason for rejecting this edit *" value={reviewOf.remark || ""} onChange={(e) => setReviewOf({ ...reviewOf, remark: e.target.value })} rows={3} data-testid="sch-reject-remark"
            className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm"/>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setReviewOf(null)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={rejectEdit} className="px-4 py-2 bg-[#C62828] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="sch-reject-edit-btn">Reject Edit</button>
          </div>
        </Dialog>
      )}

      {viewOf && (
        <Dialog title={`Schedule · ${viewOf.number}`} onClose={() => setViewOf(null)}>
          <div className="space-y-3 text-sm" data-testid="schedule-view-dialog">
            <Row label="Number" value={<span className="font-mono text-[#1565C0]">{viewOf.number}</span>}/>
            <Row label="Status" value={
              <span className="text-[10px] uppercase tracking-widest font-bold">
                {viewOf.status === "draft" && <span className="bg-[#E65100]/15 text-[#E65100] px-2 py-0.5 rounded-sm">Draft</span>}
                {viewOf.status === "open" && <span className="bg-[#2E7D32]/15 text-[#2E7D32] px-2 py-0.5 rounded-sm">Open</span>}
                {viewOf.archived && <span className="ml-1 bg-[#0D2B5E]/10 text-[#0D2B5E]/60 px-2 py-0.5 rounded-sm">Archived</span>}
                {viewOf.pending_edit && <span className="ml-1 bg-[#E65100]/15 text-[#E65100] px-2 py-0.5 rounded-sm">Edit Pending</span>}
              </span>
            }/>
            <Row label="Supplier" value={supName(viewOf.supplier_id)}/>
            <Row label="Material" value={<span><span className="font-mono text-[#1565C0]">{matCode(viewOf.material_id)}</span> {matName(viewOf.material_id)}</span>}/>
            <Row label="Period" value={`${(viewOf.period || "").toUpperCase()} · ${periodDisplay(viewOf)}`}/>
            <Row label="Expected Qty" value={<span className="font-mono">{fmtQty(viewOf.expected_qty)}</span>}/>
            <Row label="Received So Far" value={<span className="font-mono">{fmtQty(viewOf.received_so_far ?? 0)}</span>}/>
            <Row label="Variance" value={
              (() => {
                const variance = Number(viewOf.received_so_far || 0) - Number(viewOf.expected_qty || 0);
                const pct = Number(viewOf.expected_qty || 0) > 0 ? (variance / Number(viewOf.expected_qty)) * 100 : (Number(viewOf.received_so_far || 0) > 0 ? 100 : 0);
                const tone = variance === 0 ? "#0D2B5E" : variance > 0 ? "#E65100" : "#C62828";
                return <span className="font-mono font-bold" style={{ color: tone }}>{variance > 0 ? "+" : ""}{fmtQty(variance)} ({variance > 0 ? "+" : ""}{pct.toFixed(1)}%)</span>;
              })()
            }/>
            {viewOf.remaining_qty !== undefined && <Row label="Remaining" value={<span className="font-mono">{fmtQty(viewOf.remaining_qty)}</span>}/>}
            <Row label="Created" value={viewOf.created_at ? fmtIST(viewOf.created_at) : "—"}/>
            {viewOf.change_log?.length > 0 && (
              <div className="border-t border-[#EEEEEE] pt-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#0D2B5E]/60 mb-2">Change History</div>
                <div className="space-y-1 text-xs max-h-40 overflow-y-auto">
                  {viewOf.change_log.map((c, i) => (
                    <div key={i} className="bg-[#F5F5F5] p-2 rounded-sm">
                      <div className="text-[#0D2B5E]/70"><strong>{c.by}</strong> · {c.at ? fmtIST(c.at) : ""}</div>
                      {c.remark && <div className="italic mt-0.5">"{c.remark}"</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setViewOf(null)} className="px-4 py-2 bg-[#0D2B5E] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="sch-view-close">Close</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 items-center border-b border-[#EEEEEE] pb-2 last:border-b-0">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#0D2B5E]/60">{label}</div>
      <div className="col-span-2">{value}</div>
    </div>
  );
}


function SchGroupBlock({ g, expanded, onToggle, supName, matCode, matName, periodDisplay, isSA, isPHorSA, onActivate, onView, onEdit, onDelete, onApprove, onReject }) {
  const { visibleRows, controls } = usePager(g.rows, 10);
  const draftCount = g.rows.filter((r) => r.status === "draft").length;
  const archivedInGroup = g.rows.some((r) => r.archived);
  return (
    <div className="border-b border-[#EEEEEE] last:border-b-0">
      <button onClick={onToggle} className="w-full text-left px-4 py-3 bg-[#F5F5F5] hover:bg-[#1565C0]/5 flex items-center gap-3" data-testid={`sch-group-${g.key}`}>
        {expanded ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
        <span className="text-sm font-bold text-[#0D2B5E]">{g.label}</span>
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#0D2B5E]/60">{g.rows.length} schedule{g.rows.length === 1 ? "" : "s"}</span>
        {draftCount > 0 && <span className="text-[10px] uppercase tracking-widest font-bold text-[#E65100] bg-[#E65100]/10 px-2 py-0.5 rounded-sm">{draftCount} draft</span>}
        {archivedInGroup && <span className="text-[10px] uppercase tracking-widest font-bold text-[#0D2B5E]/40 bg-[#0D2B5E]/5 px-2 py-0.5 rounded-sm">Archived</span>}
      </button>
      {expanded && (
        <>
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col style={{ width: "10%" }}/><col style={{ width: "16%" }}/><col style={{ width: "18%" }}/>
              <col style={{ width: "8%" }}/><col style={{ width: "12%" }}/>
              <col style={{ width: "9%" }}/><col style={{ width: "9%" }}/><col style={{ width: "9%" }}/>
              <col style={{ width: "9%" }}/>
            </colgroup>
            <thead className="bg-white border-b border-[#EEEEEE] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-4 py-2">No.</th>
                <th className="text-left px-4 py-2">Supplier</th>
                <th className="text-left px-4 py-2">Material</th>
                <th className="text-left px-4 py-2">Period</th>
                <th className="text-left px-4 py-2">Label / Range</th>
                <th className="text-right px-4 py-2">Expected</th>
                <th className="text-right px-4 py-2">Received</th>
                <th className="text-right px-4 py-2">Variance</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r, i) => {
                // Feb-2026 #3 — show received and variance per schedule.
                const exp = Number(r.expected_qty || 0);
                const recv = Number(r.received_so_far || 0);
                const variance = recv - exp;
                const vPct = exp > 0 ? (variance / exp) * 100 : (recv > 0 ? 100 : 0);
                const vTone = variance === 0 ? "#0D2B5E" : (variance > 0 ? "#E65100" : "#C62828");
                return (
                  <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                    <td className="px-4 py-2 font-mono text-[#1565C0] truncate">
                      <button onClick={() => onView(r)} className="hover:underline" title="View schedule details" data-testid={`view-schedule-${r.number}`}>
                        {r.number}
                      </button>
                      {r.status === "draft" && <span className="ml-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold bg-[#E65100]/15 text-[#E65100] px-1.5 py-0.5 rounded-sm" data-testid={`sch-draft-${r.number}`}>Draft</span>}
                      {r.pending_edit && <span className="ml-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold bg-[#E65100]/15 text-[#E65100] px-1.5 py-0.5 rounded-sm" data-testid={`sch-pending-${r.number}`}><Clock size={10}/>Edit Pending</span>}
                      {r.archived && <span className="ml-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold bg-[#0D2B5E]/10 text-[#0D2B5E]/60 px-1.5 py-0.5 rounded-sm">Archived</span>}
                    </td>
                    <td className="px-4 py-2 truncate">{supName(r.supplier_id)}</td>
                    <td className="px-4 py-2 truncate"><span className="font-mono text-[#1565C0]">{matCode(r.material_id)}</span> {matName(r.material_id)}</td>
                    <td className="px-4 py-2 uppercase tracking-widest text-xs">{r.period}</td>
                    <td className="px-4 py-2 text-xs truncate" title={periodDisplay(r)}>{periodDisplay(r)}</td>
                    <td className="px-4 py-2 font-mono text-right">{fmtQty(exp)}</td>
                    <td className="px-4 py-2 font-mono text-right">{fmtQty(recv)}</td>
                    <td className="px-4 py-2 font-mono text-right" style={{ color: vTone }} data-testid={`sch-variance-${r.number}`}>
                      {variance > 0 ? "+" : ""}{fmtQty(variance)}
                      {exp > 0 && <div className="text-[9px] tracking-widest uppercase font-bold">{variance > 0 ? "+" : ""}{vPct.toFixed(1)}%</div>}
                    </td>
                    <td className="px-4 py-2 text-right space-x-2">
                      {r.status === "draft" && isPHorSA && (
                        <button onClick={() => onActivate(r)} className="text-[10px] tracking-widest uppercase font-bold bg-[#2E7D32] text-white px-2 py-1 rounded-sm" data-testid={`activate-draft-${r.number}`}>Activate</button>
                      )}
                      {r.pending_edit && isSA && (
                        <>
                          <button onClick={() => onApprove(r)} className="text-[#2E7D32]" title="Approve edit" data-testid={`approve-edit-${r.number}`}><CheckCircle2 size={14}/></button>
                          <button onClick={() => onReject(r)} className="text-[#C62828]" title="Reject edit" data-testid={`reject-edit-${r.number}`}>✕</button>
                        </>
                      )}
                      <button disabled={!!r.pending_edit} onClick={() => onEdit(r)} className={`${r.pending_edit ? "text-[#0D2B5E]/30 cursor-not-allowed" : "text-[#1565C0]"}`} data-testid={`edit-schedule-${r.number}`}><Pencil size={14}/></button>
                      <button onClick={() => onDelete(r)} className="text-[#C62828]" data-testid={`delete-schedule-${r.number}`}><Trash2 size={14}/></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {controls}
        </>
      )}
    </div>
  );
}
